import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicPageShell, { BulletList, SectionHeading } from '@/components/PublicPageShell';
import PopiaBadge from '@/components/PopiaBadge';
import { Telescope, Building2, Trophy, ArrowRight } from 'lucide-react';

const SCOUT_FEATURES = [
  "Search and filter by position, age group, province, PDS score, and physical metrics.",
  "Access verified player profiles, footage, match stats, and development timelines.",
  "Two-stage approval process — only verified professionals access full data.",
];

function Tier({ icon: Icon, title, price, blurb, features, testid }) {
  return (
    <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-7 hover:border-[#A3FF00]/30 transition-colors flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-7 h-7 text-[#A3FF00]" strokeWidth={1.5} />
        <h3 className="text-xl font-heading font-bold">{title}</h3>
      </div>
      <p className="text-2xl font-heading font-bold text-white">{price}</p>
      <p className="text-zinc-500 text-xs mb-4">per month</p>
      <p className="text-zinc-400 text-sm leading-relaxed flex-1">{blurb}</p>
      {features && (
        <div className="pt-4 mt-4 border-t border-zinc-800">
          <BulletList items={features} />
        </div>
      )}
    </div>
  );
}

export default function ForScouts() {
  const navigate = useNavigate();
  return (
    <PublicPageShell
      eyebrow="FOR SCOUTS, AGENCIES & ACADEMIES"
      title="Find South Africa's next generation — across all 9 provinces"
      intro="Every verified player in one searchable database. Filter by position, age, province, PDS score and physical metrics. Discover talent that has never been visible before."
      dataTestId="for-scouts-page"
    >
      <PopiaBadge className="mb-8" dataTestId="scouts-popia-badge" />
      <div className="grid lg:grid-cols-3 gap-5">
        <Tier
          icon={Telescope}
          title="Scout Lite"
          price="R350"
          testid="tier-scout-lite"
          blurb="Province & position filtering, limited profile access, basic scouting visibility — perfect for independent scouts getting started."
        />
        <Tier
          icon={Trophy}
          title="Scout Pro"
          price="R850 – R1,250"
          testid="tier-scout-pro"
          blurb="Full national access, advanced filtering, direct messaging, exports and analytics. The standard for professional scouts."
        />
        <Tier
          icon={Building2}
          title="Agency / Union / Academy"
          price="R2,500 – R10,000"
          testid="tier-agency-union"
          blurb="Multi-user accounts, recruitment dashboards, talent tracking, priority support. Built for organisations with selectors and recruitment teams."
        />
      </div>

      <SectionHeading>What every scout tier includes</SectionHeading>
      <BulletList items={SCOUT_FEATURES} />

      <SectionHeading eyebrow="WHY IT'S DIFFERENT">The first verified national database</SectionHeading>
      <div className="space-y-4 text-zinc-300 leading-relaxed">
        <p>
          Scouts have historically relied on personal networks, word-of-mouth, and tournament attendance. A gifted
          flank in Limpopo or the Eastern Cape has the same ability as one in Johannesburg — but zero of the visibility.
        </p>
        <p>
          FutureXV is the first single, verified, digital record of South Africa's developing rugby players. Stats,
          footage, fitness data, and development history are now standardised — and searchable.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        <Button onClick={() => navigate('/auth?role=scout')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-8 py-6" data-testid="scouts-join-cta">
          I'm a Scout — Get Access <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <Button onClick={() => navigate('/contact')} variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 px-8 py-6">
          Talk to sales
        </Button>
      </div>
    </PublicPageShell>
  );
}
